import React, { useState } from "react"
import {
    createStyles,
    Flex,
    useMantineTheme,
    Text,
    Loader,
    Alert,
    TextInput,
    Button,
    Group,
    CopyButton,
} from "@mantine/core"
import Markdown from "react-markdown"

import { Icon } from "quorumdesign"

// Static system prompt content
const SYSTEM_PROMPT = `Extract information from a given URL to create a personalized profile for software users that reflects their organizational stance on various issues. The model can visit multiple pages on the URL if needed.

Begin by analyzing the content of the URL for relevant information. Identify and summarize key points about the organization's stance and reasoning on various issues, and use this information to construct a detailed profile including organization name, issues they care about, their stance, and associated reasoning. Include additional details like bills they support for a comprehensive understanding.

Steps

Analyze URL Content: Review the given URL to identify key information regarding the organization's focus areas and positions.

Extract Information:

Organization Name: Identify the name of the organization.

Issue Areas: Determine the issue areas the organization prioritizes.

Stance and Reasoning: Document the stance alongside each identified issue, including any reasoning mentioned in the URL.

Additional Information: Note any additional relevant information such as supported bills.

Construct Profile: Synthesize the extracted data into a narrative format.

Output Format

Present the information in a coherent, narrative summary format, ensuring it is logical and easy to understand.

The narrative should be comprehensive and reflect all major points mentioned in the URL.

Examples

Example of extracted information from a URL discussing specific legislation:
"The Example Organization is actively supporting environmental policies, like the Climate Change Act, due to their commitment to reducing emissions and addressing climate change impacts. However, they oppose healthcare reforms such as the Healthcare Reform Bill, citing concerns over cost implications on small businesses. Their priority issues include environmental policies and healthcare reform, showing strong reasoning and alignment with their organizational goals."

Notes

Ensure the reasoning for stances is extracted directly from the URL where possible.

The output should be thorough, capturing all major points mentioned on the URL.`

const useStyles = createStyles((theme) => ({
    alert: {
        borderRadius: "8px",
        border: `1px solid ${theme.colors.violet[3]}`,
        backgroundColor: theme.colors.violet[0],
        padding: "16px",
        alignSelf: "stretch",
    },
    personalizedSummaryDetails: {
        borderRadius: "8px",
        border: "1px solid #d1d5db",
        backgroundColor: "#f0f9ff",
        padding: "16px",
        marginBottom: "16px",
        borderLeft: "4px solid #0369a1",
    },
    personalizedSummaryContent: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "16px",
        lineHeight: 1.6,
        border: "1px solid #d1d5db",
    },
    detailsGrid: {
        display: "grid",
        gap: "8px",
        fontSize: "14px",
        lineHeight: 1.5,
    },
    detailsTitle: {
        margin: "0 0 12px 0",
        color: "#0369a1",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    profileUrlSection: {
        borderRadius: "8px",
        border: "1px solid #d1d5db",
        backgroundColor: "#f8fafc",
        padding: "16px",
        marginBottom: "16px",
    },
    profileUrlTitle: {
        margin: "0 0 12px 0",
        color: "#374151",
        fontSize: "14px",
        fontWeight: 600,
    },
    promptDisplay: {
        backgroundColor: "#f8fafc",
        borderRadius: "8px",
        padding: "12px",
        border: "1px solid #e2e8f0",
        marginTop: "8px",
        fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#374151",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    summaryTitle: {
        margin: "0 0 12px 0",
        color: "#7c3aed",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    prototypeNotification: {
        borderRadius: "8px",
        border: "1px solid #f59e0b",
        backgroundColor: "#fef3c7",
        padding: "12px",
        marginBottom: "16px",
        borderLeft: "4px solid #f59e0b",
        fontSize: "14px",
        color: "#92400e",
    },
}))

const SummarySingleSentenceAlert = ({ children }) => {
    const { classes } = useStyles()
    return <div className={classes.alert}>{children}</div>
}

// Helper function to get CSRF token
const getCSRFToken = () => {
    const cookies = document.cookie.split(";")
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=")
        if (name === "csrftoken") {
            return value
        }
    }
    return null
}

// Helper function to get bill ID from URL
const getBillIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/")
    const billIndex = pathParts.indexOf("bill")
    if (billIndex !== -1 && billIndex + 1 < pathParts.length) {
        return parseInt(pathParts[billIndex + 1])
    }
    return null
}

// Helper function to validate URL
const validateUrl = (url) => {
    try {
        new URL(url)
        return true
    } catch {
        return false
    }
}

interface PersonalizedSummaryProps {
    onSummaryGenerated?: (summary: any) => void
}

export const PersonalizedSummary: React.FC<PersonalizedSummaryProps> = ({ onSummaryGenerated }) => {
    const { classes } = useStyles()
    const [personalizedSummary, setPersonalizedSummary] = useState(null)
    const [isLoadingPersonalized, setIsLoadingPersonalized] = useState(false)
    const [personalizedError, setPersonalizedError] = useState(null)
    const [profileUrl, setProfileUrl] = useState("https://www.amazon.com")
    const [urlError, setUrlError] = useState(null)
    const [isPromptExpanded, setIsPromptExpanded] = useState(false)
    const [isSystemPromptExpanded, setIsSystemPromptExpanded] = useState(false)

    // Function to generate personalized summary
    const generatePersonalizedSummary = async () => {
        const billId = getBillIdFromUrl()
        if (!billId) {
            setPersonalizedError("Could not determine bill ID from URL")
            return
        }

        // Validate URL before making request
        if (!validateUrl(profileUrl)) {
            setUrlError("Please enter a valid URL")
            return
        }

        setIsLoadingPersonalized(true)
        setPersonalizedError(null)
        setUrlError(null)

        try {
            const csrfToken = getCSRFToken()
            const response = await fetch(`/copilot/personalized_summary/generate/${billId}/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    profile_url: profileUrl,
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setPersonalizedSummary(data)
            onSummaryGenerated?.(data)
        } catch (error) {
            console.error("Error generating personalized summary:", error)
            setPersonalizedError("Failed to generate personalized summary. Please try again.")
        } finally {
            setIsLoadingPersonalized(false)
        }
    }

    return (
        <>
            <div className={classes.profileUrlSection}>
                <h4 className={classes.profileUrlTitle}>🏢 Organization Profile URL</h4>
                <Group spacing="sm">
                    <TextInput
                        placeholder="Enter organization website URL (e.g., https://www.amazon.com)"
                        value={profileUrl}
                        onChange={(event) => setProfileUrl(event.currentTarget.value)}
                        error={urlError}
                        size="sm"
                        icon={<Icon icon="link" size="sm" />}
                        style={{ flex: 1 }}
                    />
                    <Button
                        variant="filled"
                        size="sm"
                        onClick={generatePersonalizedSummary}
                        loading={isLoadingPersonalized}
                        disabled={!profileUrl.trim()}
                        leftIcon={<Icon icon="sparkles" size="sm" />}
                    >
                        Generate
                    </Button>
                </Group>
            </div>

            {isLoadingPersonalized && (
                <Flex align="center" gap="sm" p="md">
                    <Loader size="sm" />
                    <Text>Generating personalized summary...</Text>
                </Flex>
            )}

            {personalizedError && (
                <Alert color="red" icon={<Icon icon="exclamation-triangle" />}>
                    {personalizedError}
                </Alert>
            )}

            {personalizedSummary && (
                <div>
                    <div className={classes.prototypeNotification}>
                        <strong>⚠️ Prototype Feature:</strong> This is an experimental feature currently in development
                        and not ready for beta release. Results may be inconsistent and functionality may change.
                    </div>
                    <div className={classes.personalizedSummaryDetails}>
                        <h4 className={classes.detailsTitle}>
                            <span>📋</span>
                            Summary Details
                        </h4>
                        <div className={classes.detailsGrid}>
                            <p style={{ margin: 0 }}>
                                <strong>Bill ID:</strong> {personalizedSummary.bill_id || "N/A"}
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong>Bill Title:</strong> {personalizedSummary.bill_title || "N/A"}
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong>Organization URL:</strong> {profileUrl || "N/A"}
                            </p>
                            <div style={{ margin: 0 }}>
                                <Flex justify="space-between" align="center" style={{ marginBottom: "8px" }}>
                                    <strong>System Prompt:</strong>
                                    <Group spacing="xs">
                                        <CopyButton value={SYSTEM_PROMPT} timeout={2000}>
                                            {({ copied, copy }) => (
                                                <Button
                                                    variant="subtle"
                                                    size="xs"
                                                    onClick={copy}
                                                    leftIcon={<Icon icon={copied ? "check" : "copy"} size="xs" />}
                                                    color={copied ? "teal" : "gray"}
                                                >
                                                    {copied ? "Copied" : "Copy"}
                                                </Button>
                                            )}
                                        </CopyButton>
                                        <Button
                                            variant="subtle"
                                            size="xs"
                                            onClick={() => setIsSystemPromptExpanded(!isSystemPromptExpanded)}
                                            rightIcon={
                                                <Icon
                                                    icon={isSystemPromptExpanded ? "chevron-up" : "chevron-down"}
                                                    size="xs"
                                                />
                                            }
                                        >
                                            {isSystemPromptExpanded ? "Collapse" : "Expand"}
                                        </Button>
                                    </Group>
                                </Flex>
                                {isSystemPromptExpanded && <div className={classes.promptDisplay}>{SYSTEM_PROMPT}</div>}
                                {!isSystemPromptExpanded && (
                                    <div
                                        className={classes.promptDisplay}
                                        style={{ maxHeight: "60px", overflow: "hidden", position: "relative" }}
                                    >
                                        {SYSTEM_PROMPT.substring(0, 150) + (SYSTEM_PROMPT.length > 150 ? "..." : "")}
                                    </div>
                                )}
                            </div>
                            <div style={{ margin: 0 }}>
                                <Flex justify="space-between" align="center" style={{ marginBottom: "8px" }}>
                                    <strong>Prompt Used:</strong>
                                    <Group spacing="xs">
                                        <CopyButton value={personalizedSummary.prompt_text || ""} timeout={2000}>
                                            {({ copied, copy }) => (
                                                <Button
                                                    variant="subtle"
                                                    size="xs"
                                                    onClick={copy}
                                                    leftIcon={<Icon icon={copied ? "check" : "copy"} size="xs" />}
                                                    color={copied ? "teal" : "gray"}
                                                >
                                                    {copied ? "Copied" : "Copy"}
                                                </Button>
                                            )}
                                        </CopyButton>
                                        <Button
                                            variant="subtle"
                                            size="xs"
                                            onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                                            rightIcon={
                                                <Icon
                                                    icon={isPromptExpanded ? "chevron-up" : "chevron-down"}
                                                    size="xs"
                                                />
                                            }
                                        >
                                            {isPromptExpanded ? "Collapse" : "Expand"}
                                        </Button>
                                    </Group>
                                </Flex>
                                {isPromptExpanded && (
                                    <div className={classes.promptDisplay}>
                                        {personalizedSummary.prompt_text || "N/A"}
                                    </div>
                                )}
                                {!isPromptExpanded && (
                                    <div
                                        className={classes.promptDisplay}
                                        style={{ maxHeight: "60px", overflow: "hidden", position: "relative" }}
                                    >
                                        {personalizedSummary.prompt_text
                                            ? personalizedSummary.prompt_text.substring(0, 150) +
                                              (personalizedSummary.prompt_text.length > 150 ? "..." : "")
                                            : "N/A"}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <h4 className={classes.summaryTitle}>
                            <span>✨</span>
                            Personalized Summary
                        </h4>
                        <div className={classes.personalizedSummaryContent}>
                            <Markdown>{personalizedSummary.summary}</Markdown>
                        </div>
                    </div>

                    {personalizedSummary.one_sentence_summary && (
                        <SummarySingleSentenceAlert>
                            <strong>Personalized Key Takeaway: </strong>
                            {personalizedSummary.one_sentence_summary}
                        </SummarySingleSentenceAlert>
                    )}
                </div>
            )}
        </>
    )
}
